import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
import { usePermissions, MODULES } from "../features/auth/model/usePermissions";
import styles from "./Sidebar.module.css";

// ── Asset imports ─────────────────────────────────────────────────────────────
import logoDashboard     from "../assets/images/Logo_Dashboard.png";
import logoDashboardMini from "../assets/images/Logo_Dashboard_Mini.png";

import { ReactComponent as ArrowIcon }      from "../assets/icons/left-arrow-SideBar.svg";
import { ReactComponent as DashboardIcon }  from "../assets/icons/dashboard.svg";
import { ReactComponent as ConnectIcon }    from "../assets/icons/connect.svg";
import { ReactComponent as NodeIcon }       from "../assets/icons/node.svg";
import { ReactComponent as UsersIcon }      from "../assets/icons/users.svg";
import { ReactComponent as PoliciesIcon }   from "../assets/icons/policies.svg";
import { ReactComponent as ComplianceIcon } from "../assets/icons/compliance.svg";
import { ReactComponent as AnalyticsIcon }  from "../assets/icons/analytics.svg";
import { ReactComponent as SettingsIcon }   from "../assets/icons/settings.svg";

import { ReactComponent as LogoutIcon }    from "../assets/icons/logout.svg";

// ── Nav items ─────────────────────────────────────────────────────────────────
// `module` ties a link to an RBAC module so it can be hidden when the role has
// None access. Links without a `module` (Dashboard, Connect, Compliance) always show.
const navItems = [
  { id: "dashboard",  label: "Dashboard",  Icon: DashboardIcon  },
  { id: "connect",    label: "Connect",    Icon: ConnectIcon    },
  { id: "nodes",      label: "Nodes",      Icon: NodeIcon,       module: MODULES.NODE_MONITORING   },
  { id: "users",      label: "Users",      Icon: UsersIcon,      module: MODULES.USER_MANAGEMENT   },
  { id: "policies",   label: "Policies",   Icon: PoliciesIcon,   module: MODULES.POLICY_MANAGEMENT },
  { id: "compliance", label: "Compliance", Icon: ComplianceIcon },
  { id: "analytics",  label: "Analytics",  Icon: AnalyticsIcon,  module: MODULES.ANALYTICS         },
  { id: "settings",   label: "Settings",   Icon: SettingsIcon,   module: MODULES.SETTINGS          },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function Sidebar() {
  const { collapsed, toggle } = useSidebar();
  const { user, logout } = useAuth();
  const { canAccess } = usePermissions();
  const location = useLocation();

  // Hide RBAC modules the current role can't access (None). Non-module links stay.
  const visibleItems = navItems.filter(item => !item.module || canAccess(item.module));

  const currentPath =
    location.pathname === "/" ? "dashboard" : location.pathname.replace("/", "");

  // Derive display name and initials from the user object
  const displayName = user?.name ?? user?.username ?? user?.email ?? "User";
  const role        = user?.role ?? "Admin";
  const initials    = displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  // When collapsed, clicking anywhere on the sidebar re-opens it
  const handleSidebarClick = () => {
    if (collapsed) toggle();
  };

  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}
      onClick={handleSidebarClick}
      style={collapsed ? { cursor: "pointer" } : undefined}
    >
      {/* ── Logo row ── */}
      <div className={styles.logoRow}>
        <div className={styles.logoWrap}>
          <img
            src={collapsed ? logoDashboardMini : logoDashboard}
            alt="NPQ VPN"
            className={collapsed ? styles.logoImgMini : styles.logoImg}
          />
        </div>

        {/* Toggle button — only visible when expanded */}
        {!collapsed && (
          <button
            className={styles.toggleBtn}
            onClick={toggle}
            aria-label="Collapse sidebar"
          >
            <ArrowIcon className={styles.toggleIcon} />
          </button>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav className={styles.nav}>
        {visibleItems.map((item) => {
          const isActive =
            currentPath === item.id ||
            (item.id === "dashboard" && location.pathname === "/");

          return (
            <Link
              key={item.id}
              to={item.id === "dashboard" ? "/" : `/${item.id}`}
              data-label={item.label}
              className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
            >
              <span className={styles.navIcon}>
                <item.Icon />
              </span>
              <span className={styles.navLabel}>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* ── User row ── */}
      <div className={styles.userRow}>
        <div className={styles.avatar}>{initials}</div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{displayName}</div>
          <div className={styles.userRole}>{role}</div>
        </div>
        <button
          className={styles.logoutBtn}
          onClick={(e) => { e.stopPropagation(); logout(); }}
          title="Log out"
        >
          <LogoutIcon className={styles.logoutIcon} />
        </button>
      </div>
    </aside>
  );
}
